/**
 * Logout Utilities
 *
 * Comprehensive logout functionality that can be used throughout the application
 * to ensure complete cleanup of user data, cookies, and storage.
 */

import { getCurrentLocaleFromPath } from '@/utils/language-utils';
import Cookies from 'js-cookie';

// Helper function to validate and sanitize redirect URLs
const getSafeRedirectUrl = (redirectUrl: string): string => {
  // List of unsafe redirect paths that should redirect to home instead
  const unsafePaths = [
    '/logout',
    '/login',
    '/signup',
    '/forgot-password',
    '/reset-password'
  ];

  // Handle null, undefined, or empty strings
  if (!redirectUrl || typeof redirectUrl !== 'string') {
    return '/';
  }

  // Check if redirect URL is in unsafe list
  if (unsafePaths.some(path => redirectUrl.includes(path))) {
    return '/';
  }

  // Check if it's an external URL (starts with http/https)
  if (redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://')) {
    // For external URLs, extract the pathname only
    try {
      const url = new URL(redirectUrl);
      const pathname = url.pathname;
      return getSafeRedirectUrl(pathname); // Recursive call with pathname only
    } catch {
      return '/';
    }
  }

  // Ensure it starts with / for internal URLs
  if (!redirectUrl.startsWith('/')) {
    return '/';
  }

  // Return the safe redirect URL
  return redirectUrl;
};

/**
 * Comprehensive function to clear all user-related data
 * This function can be called independently of the useAuth hook
 */
export const performCompleteLogout = () => {
  try {
    console.log('🚀 Starting complete logout cleanup...');

    // Clear all cookies
    clearAllCookies();

    // Clear localStorage
    clearUserLocalStorage();

    // Clear sessionStorage
    clearUserSessionStorage();

    console.log('✅ Complete logout cleanup finished');
  } catch (error) {
    console.error('❌ Error during logout cleanup:', error);
  }
};

/**
 * Clear all user-related cookies
 */
const clearAllCookies = () => {
  try {
    const cookiesToClear = [
      'access_token',
      'refresh_token',
      'session_id',
      'user_id',
      'auth_token',
      'jwt_token',
    ];

    cookiesToClear.forEach(cookieName => {
      // Method 1: Using js-cookie library
      Cookies.remove(cookieName, { path: '/' });

      // Try different domain variations
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        Cookies.remove(cookieName, { path: '/', domain: hostname });
        Cookies.remove(cookieName, { path: '/', domain: `.${hostname}` });
      }

      // Method 2: Using document.cookie (more thorough)
      if (typeof document !== 'undefined') {
        const paths = ['/', '/dashboard', '/admin', '/account', '/auth'];
        paths.forEach(path => {
          document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Strict`;

          if (typeof window !== 'undefined') {
            const hostname = window.location.hostname;
            document.cookie = `${cookieName}=; path=${path}; domain=${hostname}; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Strict`;
            document.cookie = `${cookieName}=; path=${path}; domain=.${hostname}; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Strict`;
          }
        });
      }
    });

    console.log('✅ Cookies cleared');
  } catch (error) {
    console.warn('⚠️ Error clearing cookies:', error);
  }
};

/**
 * Clear user-related localStorage items
 */
const clearUserLocalStorage = () => {
  try {
    if (typeof localStorage === 'undefined') return;

    const localStorageKeysToRemove = [
      // Authentication related
      'rememberMe',
      'rememberedUsername',
      'auth_cache',
      'user_session',

      // User preferences (optional - you might want to keep theme)
      'user-preferences',
      'user-settings',
      'user_profile',

      // Application specific
      'dashboard_settings',
      'sidebar_state',
      'recent_activities',

      // Theme (uncomment if you want to clear theme on logout)
      // 'theme-storage',
    ];

    localStorageKeysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn('Failed to remove localStorage key: %s', key, error);
      }
    });

    console.log('✅ localStorage cleared');
  } catch (error) {
    console.warn('⚠️ Error clearing localStorage:', error);
  }
};

/**
 * Clear user-related sessionStorage items
 */
const clearUserSessionStorage = () => {
  try {
    if (typeof sessionStorage === 'undefined') return;

    const sessionStorageKeysToRemove = [
      'auth_cache',
      'auth_check_in_progress',
      'user_session',
      'temp_data',
      'form_data',
      'navigation_state',
      'manually-chosen-locale',
      'locale-redirected',
    ];

    sessionStorageKeysToRemove.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.warn('Failed to remove sessionStorage key: %s', key, error);
      }
    });

    // Alternative: Clear all sessionStorage
    try {
      sessionStorage.clear();
    } catch (error) {
      console.warn('Failed to clear sessionStorage:', error);
    }

    console.log('✅ sessionStorage cleared');
  } catch (error) {
    console.warn('⚠️ Error clearing sessionStorage:', error);
  }
};

/**
 * Force reload the page after logout (optional)
 * This ensures all JavaScript state is completely reset
 */
export const forcePageReload = () => {
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

/**
 * Redirect to login page with safe redirect URL
 */
export const redirectToLogin = (currentPath?: string) => {
  if (typeof window !== 'undefined') {
    if (currentPath) {
      const safeRedirectUrl = getSafeRedirectUrl(currentPath);
      // Only add redirect parameter if it's not the home page
      if (safeRedirectUrl === '/') {
        window.location.href = '/login';
      } else {
        window.location.href = `/login?redirect=${encodeURIComponent(safeRedirectUrl)}`;
      }
    } else {
      window.location.href = '/login';
    }
  }
};

/**
 * Redirect to logout page instead of calling logout API directly
 * This provides a better user experience with visual feedback
 */
export const redirectToLogoutPage = (
  options: {
    redirectAfterLogout?: string;
    currentPath?: string;
  } = {}
) => {
  if (typeof window !== 'undefined') {
    const { redirectAfterLogout, currentPath } = options;
    const currentLocale = getCurrentLocaleFromPath(window.location.pathname);

    // Build logout page URL with locale
    const logoutPath = currentLocale === 'en' ? '/logout' : `/${currentLocale}/logout`;

    // Use current page as redirect if not specified
    const redirectUrl = redirectAfterLogout || currentPath || window.location.pathname;

    // Add redirect parameter with current page
    if (redirectUrl) {
      const safeRedirectUrl = getSafeRedirectUrl(redirectUrl);
      if (safeRedirectUrl !== '/' && safeRedirectUrl !== '/login') {
        window.location.href = `${logoutPath}?redirect=${encodeURIComponent(safeRedirectUrl)}`;
        return;
      }
    }

    // Default redirect to logout page without redirect parameter
    window.location.href = logoutPath;
  }
};

/**
 * Complete logout with redirect
 * This is the main function to call for a complete logout
 */
export const logoutAndRedirect = (
  options: {
    redirectToLogin?: boolean;
    forceReload?: boolean;
    currentPath?: string;
  } = {}
) => {
  const { redirectToLogin: shouldRedirect = true, forceReload = false } = options;

  // Perform complete cleanup
  performCompleteLogout();

  if (forceReload) {
    // Force page reload (most thorough)
    forcePageReload();
  } else if (shouldRedirect) {
    // Redirect to login page
    setTimeout(() => {
      const currentLocale = getCurrentLocaleFromPath(window.location.pathname);
      const newPath = currentLocale === 'en' ? '/login' : `/${currentLocale}/login`;
      window.location.href = newPath;
    }, 1000);
  }
};
